import React from 'react';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  MessageSquare,
  Bell,
  Send,
  BookOpen,
  Users,
  Megaphone,
  TrendingUp,
  Shield,
  BarChart3,
  Bot,
  X,
  DollarSign,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@mellify/ui';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '@mellify/shared';
import { formatDistanceToNow } from 'date-fns';

export interface NotificationCardProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDismiss: (id: string) => void;
  compact?: boolean;
}

/**
 * Map notification type to icon and color
 */
function getNotificationTypeConfig(type: string): {
  icon: React.ReactNode;
  colorClass: string;
} {
  switch (type) {
    case 'escalation':
      return {
        icon: <AlertTriangle className="w-4 h-4" />,
        colorClass: 'text-warning-500 bg-warning-50 dark:bg-warning-500/10',
      };
    case 'approval_request':
      return {
        icon: <CheckCircle className="w-4 h-4" />,
        colorClass: 'text-primary-500 bg-primary-50 dark:bg-primary-500/10',
      };
    case 'approval_decision':
      return {
        icon: <CheckCircle className="w-4 h-4" />,
        colorClass: 'text-success-500 bg-success-50 dark:bg-success-500/10',
      };
    case 'publish_success':
      return {
        icon: <Send className="w-4 h-4" />,
        colorClass: 'text-success-500 bg-success-50 dark:bg-success-500/10',
      };
    case 'publish_failed':
      return {
        icon: <XCircle className="w-4 h-4" />,
        colorClass: 'text-danger-500 bg-danger-50 dark:bg-danger-500/10',
      };
    case 'crisis_alert':
      return {
        icon: <Shield className="w-4 h-4" />,
        colorClass: 'text-danger-500 bg-danger-50 dark:bg-danger-500/10',
      };
    case 'comment_received':
    case 'dm_received':
      return {
        icon: <MessageSquare className="w-4 h-4" />,
        colorClass: 'text-info-500 bg-info-50 dark:bg-info-500/10',
      };
    case 'report_ready':
      return {
        icon: <BarChart3 className="w-4 h-4" />,
        colorClass: 'text-primary-500 bg-primary-50 dark:bg-primary-500/10',
      };
    case 'kb_updated':
    case 'kb_processing_complete':
      return {
        icon: <BookOpen className="w-4 h-4" />,
        colorClass: 'text-info-500 bg-info-50 dark:bg-info-500/10',
      };
    case 'token_expiring':
      return {
        icon: <AlertTriangle className="w-4 h-4" />,
        colorClass: 'text-warning-500 bg-warning-50 dark:bg-warning-500/10',
      };
    case 'team_invite':
      return {
        icon: <Users className="w-4 h-4" />,
        colorClass: 'text-primary-500 bg-primary-50 dark:bg-primary-500/10',
      };
    case 'campaign_started':
    case 'campaign_completed':
      return {
        icon: <Megaphone className="w-4 h-4" />,
        colorClass: 'text-secondary-500 bg-secondary-50 dark:bg-secondary-500/10',
      };
    case 'trend_alert':
      return {
        icon: <TrendingUp className="w-4 h-4" />,
        colorClass: 'text-info-500 bg-info-50 dark:bg-info-500/10',
      };
    case 'system':
      return {
        icon: <Bot className="w-4 h-4" />,
        colorClass: 'text-dark-500 bg-dark-50 dark:bg-dark-500/10',
      };

    // ─── Ad Management Notification Types ─────────────────────────────
    case 'ad_approved':
      return {
        icon: <CheckCircle className="w-4 h-4" />,
        colorClass: 'text-success-500 bg-success-50 dark:bg-success-500/10',
      };
    case 'ad_rejected':
      return {
        icon: <XCircle className="w-4 h-4" />,
        colorClass: 'text-danger-500 bg-danger-50 dark:bg-danger-500/10',
      };
    case 'ad_budget_depleted':
      return {
        icon: <DollarSign className="w-4 h-4" />,
        colorClass: 'text-warning-500 bg-warning-50 dark:bg-warning-500/10',
      };
    case 'ad_account_issue':
      return {
        icon: <AlertTriangle className="w-4 h-4" />,
        colorClass: 'text-danger-500 bg-danger-50 dark:bg-danger-500/10',
      };
    case 'ad_performance_alert':
      return {
        icon: <BarChart3 className="w-4 h-4" />,
        colorClass: 'text-warning-500 bg-warning-50 dark:bg-warning-500/10',
      };
    case 'ad_sync_failed':
      return {
        icon: <RefreshCw className="w-4 h-4" />,
        colorClass: 'text-danger-500 bg-danger-50 dark:bg-danger-500/10',
      };

    default:
      return {
        icon: <Bell className="w-4 h-4" />,
        colorClass: 'text-dark-500 bg-dark-50 dark:bg-dark-500/10',
      };
  }
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onMarkAsRead,
  onDismiss,
  compact = false,
}) => {
  const navigate = useNavigate();
  const { icon, colorClass } = getNotificationTypeConfig(notification.type);

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
  });

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss(notification.id);
  };

  return (
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'group relative flex gap-3 rounded-lg border transition-colors duration-150 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        compact ? 'p-3' : 'p-4',
        notification.read
          ? 'bg-white dark:bg-dark-900 border-dark-200 dark:border-dark-700 hover:bg-dark-50 dark:hover:bg-dark-800'
          : 'bg-primary-50/30 dark:bg-primary-500/5 border-primary-200 dark:border-primary-500/20 hover:bg-primary-50/50 dark:hover:bg-primary-500/10'
      )}
    >
      {/* Unread indicator */}
      {!notification.read && (
        <span className="absolute top-3 left-1.5 flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
        </span>
      )}

      {/* Type icon */}
      <div
        className={cn(
          'shrink-0 flex items-center justify-center rounded-lg',
          compact ? 'w-8 h-8' : 'w-10 h-10',
          colorClass
        )}
      >
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4
            className={cn(
              'font-medium text-dark-900 dark:text-dark-100 leading-tight',
              compact ? 'text-sm' : 'text-sm',
              !notification.read && 'font-semibold'
            )}
          >
            {notification.title}
          </h4>

          {/* Dismiss button */}
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 p-1 rounded text-dark-400 dark:text-dark-500 hover:text-dark-600 dark:hover:text-dark-300 hover:bg-dark-100 dark:hover:bg-dark-700 opacity-0 group-hover:opacity-100 transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:opacity-100"
            aria-label="Dismiss notification"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <p
          className={cn(
            'text-dark-500 dark:text-dark-400 mt-0.5 leading-snug',
            compact ? 'text-xs line-clamp-2' : 'text-sm line-clamp-3'
          )}
        >
          {notification.message}
        </p>

        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-xs text-dark-400 dark:text-dark-500">
            {timeAgo}
          </span>

          {notification.actionUrl && !compact && (
            <span className="text-xs text-primary-500 hover:text-primary-600 font-medium">
              View details →
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

NotificationCard.displayName = 'NotificationCard';